import { useEffect, useMemo } from 'react';

import { Control, FieldPath, FieldValues, useWatch } from 'react-hook-form';

import { EBundlesViewModel } from '@appcharge/shared-ui';
import { EmblemType, RibbonType } from 'common/contracts';

import { IDropdownOption } from '../components/sections';
import { BadgeTypeFilter } from '../Offer.consts';

import { useBadgeForm } from './useBadgeForm';

interface IUseBadgeFormFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  fieldName: FieldPath<TFieldValues>;
  publisherId: string | undefined;
  relevantBadgeType: BadgeTypeFilter;
  viewModel: EBundlesViewModel | undefined;
  initialBadgeId: string | null | undefined;
}

interface IUseBadgeFormFieldReturn {
  badgeListOptions: IDropdownOption[];
  emblemBadges: EmblemType[];
  ribbonBadges: RibbonType[];
  selectedPublisherBadgeId: string;
}

/**
 * Binds a react-hook-form badge field to a useBadgeForm instance:
 *  - Calls useBadgeForm with the provided initial badge id.
 *  - Watches the form's badge field and syncs its value into useBadgeForm.
 *  - Maps the raw badge list into dropdown options.
 *
 * The watched field value is normalized: empty strings are treated as
 * "no selection" (null), mirroring how BadgesSection clears the form value
 * when the user picks the "None" option.
 *
 * Pair with getInitialBadgeValues to derive initialBadgeId from the loaded offer.
 *
 * @example
 * const initialBadgeValues = useMemo(
 *   () => getInitialBadgeValues(currentOffer?.badges, createBadgeFilter(BADGE_TYPE_FILTER_ALL, undefined)),
 *   [currentOffer?.badges]
 * );
 * const { badgeListOptions, emblemBadges, ribbonBadges, selectedPublisherBadgeId } = useBadgeFormField({
 *   control: form.control,
 *   fieldName: 'badge',
 *   publisherId,
 *   relevantBadgeType: BADGE_TYPE_FILTER_ALL,
 *   viewModel: undefined,
 *   initialBadgeId: initialBadgeValues.badgeId
 * });
 */
export const useBadgeFormField = <TFieldValues extends FieldValues>({
  control,
  fieldName,
  publisherId,
  relevantBadgeType,
  viewModel,
  initialBadgeId
}: IUseBadgeFormFieldProps<TFieldValues>): IUseBadgeFormFieldReturn => {
  const {
    badgeList,
    emblemBadges,
    ribbonBadges,
    setSelectedBadgeId,
    selectedPublisherBadgeId
  } = useBadgeForm({
    publisherId,
    relevantBadgeType,
    viewModel,
    initialSelectedBadgeId: initialBadgeId
  });

  const watchedBadge = useWatch({ control, name: fieldName });

  useEffect(() => {
    let nextSelectedId: string | null = null;
    if (typeof watchedBadge === 'string' && watchedBadge !== '') {
      nextSelectedId = watchedBadge;
    }
    setSelectedBadgeId(nextSelectedId);
  }, [watchedBadge, setSelectedBadgeId]);

  const badgeListOptions = useMemo<IDropdownOption[]>(
    () =>
      badgeList?.map((item) => ({
        value: item.value,
        label: item.content
      })) || [],
    [badgeList]
  );

  return {
    badgeListOptions,
    emblemBadges,
    ribbonBadges,
    selectedPublisherBadgeId
  };
};
